import React, { useEffect, useState } from 'react';
import { FormControlLabel, Radio, RadioGroup, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Modal from 'Components/Molecules/Modal';
import Typography from 'Components/Atoms/Typography';
import Box from 'Components/Atoms/Box';
import { formatCurrency, formatDate } from 'App/Group/format';
import {
  CouponChangeDecision,
  CouponChangeDialogRequest,
  CouponChangeSummary,
  registerCouponChangeDialog,
} from './couponChange';
import '../styles.scss';

const CONDITION_LABEL: Record<string, string> = {
  minGuests: 'minimum party size',
  maxGuests: 'maximum party size',
  occassion: 'occasion',
  visitFrom: 'visit period (start)',
  visitTill: 'visit period (end)',
  weekday: 'weekday',
  earliestTime: 'earliest time',
  latestTime: 'latest time',
};

/** Mounted once in the app shell; save flows ask it through reviewCouponChange. */
const CouponChangeDialog: React.FC = () => {
  const { t, i18n } = useTranslation('coupons');
  const lang = i18n.language;
  const [request, setRequest] = useState<CouponChangeDialogRequest | null>(null);
  const [choice, setChoice] = useState<'' | 'goodwill' | 'withdraw'>('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);

  useEffect(() => {
    registerCouponChangeDialog((next) => {
      setChoice('');
      setNote('');
      setTried(false);
      // Only one comparison is shown at a time; an earlier caller gets "cancelled".
      setRequest((previous) => {
        previous?.resolve(null);
        return next;
      });
    });
    return () => {
      registerCouponChangeDialog(null);
      setRequest((previous) => {
        previous?.resolve(null);
        return null;
      });
    };
  }, []);

  if (!request) return null;

  const needsDecision = !!request.withoutGoodwill.evaluation?.unmetConditions.length;
  const shown: CouponChangeSummary =
    choice === 'goodwill' && request.withGoodwill ? request.withGoodwill : request.withoutGoodwill;
  const evaluation = shown.evaluation!;
  const previous = shown.previous!;
  const next = shown.next!;
  const money = (value: number) => formatCurrency(value, 'EUR', lang);

  const close = (decision: CouponChangeDecision | null) => {
    request.resolve(decision);
    setRequest((current) => (current === request ? null : current));
  };

  const submit = () => {
    setTried(true);
    if (needsDecision && !choice) return;
    close({ decision: needsDecision ? (choice as 'goodwill' | 'withdraw') : null, note, summary: shown });
  };

  const changes: string[] = [];
  if (previous.guests !== next.guests) {
    changes.push(t('guests from {{from}} to {{to}}', { from: previous.guests, to: next.guests }));
  }
  if (previous.date !== next.date) {
    changes.push(t('date from {{from}} to {{to}}', { from: formatDate(previous.date, lang), to: formatDate(next.date, lang) }));
  }
  if (previous.occassion !== next.occassion) changes.push(t('the occasion'));
  if (Math.round(evaluation.before.grossAmount * 100) !== Math.round(next.grossAmount * 100)) {
    changes.push(t('the ticket selection'));
  }

  const open = evaluation.openAmount;
  const goodwillDiscount = request.withGoodwill?.evaluation?.after.discount ?? 0;

  return (
    <Modal
      open
      maxWidth="sm"
      fullWidth
      stopAwayClick
      onClose={() => close(null)}
      title="Check the discount code"
      titleTranslation="coupons"
      severity={needsDecision ? 'warning' : 'info'}
      dontCloseAfterSubmit
      actions={[
        { id: 'back', label: 'Back to editing', translation: 'coupons', variant: 'default', onClick: () => close(null) },
        { id: 'save', label: 'Save change', translation: 'coupons', variant: 'primary', onClick: submit },
      ]}
    >
      <Typography variant="text-2" block translation={null}>
        {t('This booking redeemed {{code}}. You are changing {{changes}}. The discount is recalculated with the terms promised at payment.', {
          code: shown.code,
          changes: changes.length > 1
            ? `${changes.slice(0, -1).join(', ')} ${t('and')} ${changes[changes.length - 1]}`
            : changes[0] ?? t('the booking'),
        })}
      </Typography>

      <table className="coupon-compare">
        <thead>
          <tr>
            <th scope="col" />
            <th scope="col">{t('Before')}</th>
            <th scope="col">{t('After')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{t('Ticket price')}</th>
            <td>{money(evaluation.before.grossAmount)}</td>
            <td>{money(evaluation.after.grossAmount)}</td>
          </tr>
          <tr>
            <th scope="row">{t('Discount {{code}}', { code: shown.code })}</th>
            <td>− {money(evaluation.before.discount)}</td>
            <td>− {money(evaluation.after.discount)}</td>
          </tr>
          <tr className="strong">
            <th scope="row">{t('Total price')}</th>
            <td>{money(evaluation.before.netAmount)}</td>
            <td>{money(evaluation.after.netAmount)}</td>
          </tr>
          <tr>
            <th scope="row">{t('Paid so far')}</th>
            <td colSpan={2}>{money(evaluation.paidAmount)}</td>
          </tr>
          <tr className="strong">
            <th scope="row">{open < 0 ? t('Paid too much') : t('Still open')}</th>
            <td colSpan={2}>{money(Math.abs(open))}</td>
          </tr>
        </tbody>
      </table>

      {needsDecision && (
        <Box className="coupon-callout warning" role="group" aria-label={t('Decision about the discount')}>
          <Typography variant="text-3" weight="bold" block translation={null}>
            {t('No longer met after the change: {{list}}.', {
              list: request.withoutGoodwill.evaluation!.unmetConditions
                .map((code) => t(CONDITION_LABEL[code] ?? code))
                .join(', '),
            })}
          </Typography>
          <RadioGroup value={choice} onChange={(event) => setChoice(event.target.value as 'goodwill' | 'withdraw')}>
            <FormControlLabel
              value="goodwill"
              control={<Radio color="primary" />}
              label={t('Goodwill: keep the discount on the new booking ({{amount}})', { amount: money(goodwillDiscount) })}
            />
            <FormControlLabel
              value="withdraw"
              control={<Radio color="primary" />}
              label={t('The discount no longer applies ({{amount}})', { amount: money(0) })}
            />
          </RadioGroup>
          {tried && !choice && (
            <div role="alert"><Typography variant="text-3" color="error" block translation="coupons">
              Choose how the discount continues.
            </Typography></div>
          )}
          {choice === 'goodwill' && (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={t('Note for the log (optional)')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              inputProps={{ maxLength: 500 }}
              style={{ marginTop: 8 }}
            />
          )}
        </Box>
      )}

      <Typography variant="text-3" color="subdued" block translation="coupons" style={{ marginTop: 12 }}>
        Nothing is charged or refunded automatically. Collect or refund a difference through the usual payment options. The guest receives an updated confirmation with the new total, discount, payments and open amount.
      </Typography>
    </Modal>
  );
};

export default CouponChangeDialog;
